import {
	Combobox,
	InputBase,
	InputPlaceholder,
	useCombobox,
} from '@mantine/core';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { matchPath, useNavigate } from 'react-router-dom';
import { useOrganisation } from '../hooks/useOrganisation';
import { axiosInstance } from '../libs/axiosConfig';
import type { OrganisationDto } from '../util/types/OrganisationDto';
import type { ApiResponse } from '../util/types/Response';

export function OrganisationSelector() {
	const navigate = useNavigate();
	const organisation = useOrganisation();

	const orgQuery = organisationQuery();
	const currentUrl = matchPath('/:organisation/*', window.location.pathname);

	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
	});

	const updateSelectedOrg = (urlOrg: string) => {
		if (orgQuery.data?.isValid) {
			organisation.set(urlOrg);
			navigate(`/${urlOrg}/${currentUrl?.params['*']}`);
		}
	};

	const findOrganisationDisplayValue = (urlOrg: string) => {
		const org = orgQuery.data?.value.find(
			(org) => org.urlValue === urlOrg.toLowerCase(),
		);

		return org?.name;
	};

	const organisationForDisplay = useMemo(() => {
		if (currentUrl?.params.organisation && orgQuery.isSuccess) {
			return findOrganisationDisplayValue(currentUrl?.params.organisation);
		}
	}, [orgQuery.isSuccess, currentUrl?.params.organisation]);

	return (
		<Combobox
			store={combobox}
			withinPortal={false}
			onOptionSubmit={(val) => {
				updateSelectedOrg(val);
				combobox.closeDropdown();
			}}
		>
			<Combobox.Target>
				<InputBase
					id={'organisation-selector'}
					component={'button'}
					type={'button'}
					pointer
					rightSectionPointerEvents={'none'}
					onClick={() => combobox.toggleDropdown()}
					w={'250px'}
					variant={'filled'}
					leftSection={<Combobox.Chevron />}
				>
					{organisationForDisplay ?? (
						<InputPlaceholder>Loading...</InputPlaceholder>
					)}
				</InputBase>
			</Combobox.Target>

			<Combobox.Dropdown>
				<Combobox.Options>
					{orgQuery.data?.value && orgQuery.data.value.length > 0 ? (
						orgQuery.data?.value.map((org) => (
							<Combobox.Option key={org.urlValue} value={org.urlValue}>
								{org.name}
							</Combobox.Option>
						))
					) : (
						<InputPlaceholder>Loading...</InputPlaceholder>
					)}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
}

async function get(path: string) {
	return await axiosInstance
		.get(path)
		.then((res) => res.data as ApiResponse<OrganisationDto[]>);
}

export const organisationQuery = () => {
	return useQuery({
		queryKey: ['organisation/all'], //This needs to be changed
		queryFn: async () => {
			const data = await get('organisation/all');

			return data;
		},
		onError: (e) => {
			console.log(e);
		},
		retry: false,
	});
};
